import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { setBoostsModal, updateBoosts } from '../../redux/features/app/app';
import Modal from './Modal';
import { BoostsModalType } from '../../redux/store/IStore';
import Add from '@mui/icons-material/Add';
import Search from '@mui/icons-material/Search';
import {
  createBoostPhraseItems,
  deletePhraseSet,
  getPhrases,
  getSetPhrases,
  IBoostPhrase,
  IPhraseSet,
  LocalBoostSets,
  patchPhraseSetById,
  PhraseType,
  PhraseVisibility,
  postPhraseSet,
  SetPhrases,
  updateBoostSetItems,
} from '../../api/PhraseService';
import Tooltip from '@mui/material/Tooltip';

import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import PublicIcon from '@mui/icons-material/Public';
import DeleteIcon from '@mui/icons-material/Delete';
import { enqueueSnackbar } from 'notistack';
import { cloneDeep } from 'lodash';
import Delete from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import { UploadFile } from '@mui/icons-material';
import { DateTime } from 'luxon';

const priorities = {
  PERSONAL: 0,
  SHARED: 1,
  GLOBAL: 2,
};

const BoostModal = () => {
  const dispatch = useAppDispatch();
  const { visible, type } = useAppSelector((state) => state.app.boostsModal);
  const {
    boostSets,
    activeBoostSet,
    originalActiveBoostSet,
    activeBoostSetItems,
    originalActiveBoostSetItems,
    activeBoostSetShared,
    newBoostText,
    newBoostValue,
  } = useAppSelector((state) => state.app.boosts);

  const [boostSetsFilter, setBoostSetsFilter] = useState<string>('');
  const updateBoostSetFilter = (event: ChangeEvent<HTMLInputElement>) => {
    setBoostSetsFilter(event.target.value);
  };

  const [boostActiveSetFilter, setBoostActiveSetFilter] = useState<string>('');
  const updateActiveBoostSetFilter = (event: ChangeEvent<HTMLInputElement>) => {
    setBoostActiveSetFilter(event.target.value);
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const hideBoostModal = () => {
    dispatch(setBoostsModal({ visible: false }));
  };

  const changeToCreateSetType = () => {
    dispatch(setBoostsModal({ type: BoostsModalType.create_set }));
    dispatch(
      updateBoosts({
        activeBoostSet: {
          id: -1,
          isOwner: true,
          name: '',
          type: PhraseType.BOOST,
          visibility: PhraseVisibility.PERSONAL,
        },
      })
    );
  };

  const changeToEditSetType = (set: IPhraseSet) => {
    dispatch(setBoostsModal({ type: BoostsModalType.edit_set }));
    dispatch(updateBoosts({ activeBoostSet: set, originalActiveBoostSet: set }));
  };

  const changeToDeleteSetType = (set: IPhraseSet) => {
    dispatch(updateBoosts({ activeBoostSet: set }));
    dispatch(setBoostsModal({ type: BoostsModalType.delete_set }));
  };

  const toggleBoostSet = (index: number) => {
    if (!boostSets) return;
    if (boostSets.length <= index) return;

    const copy = cloneDeep(boostSets);

    copy[index].checked = !copy[index].checked;

    localStorage.setItem('boost_sets', JSON.stringify(copy));

    dispatch(updateBoosts({ boostSets: copy }));
  };

  const fetchBoostSets = async () => {
    try {
      const boostsResponse = await getPhrases();

      const localBoosts: LocalBoostSets = boostSets
        ? boostsResponse.data.map((set) => {
            const match = boostSets.find((prev) => prev.set.id === set.id);
            return { checked: match?.checked ?? false, set };
          })
        : boostsResponse.data.map((set) => {
            return { checked: false, set };
          });

      localBoosts.sort((a, b) => priorities[a.set.type] - priorities[b.set.type]);

      localStorage.setItem('boost_sets', JSON.stringify(localBoosts));

      dispatch(updateBoosts({ boostSets: localBoosts }));
    } catch (error) {
      enqueueSnackbar('Pri pridobivanju skupin uteži je prišlo do napake.', { variant: 'error' });
    }
  };

  const fetchBoostSet = async () => {
    if (!activeBoostSet) return;

    try {
      const replacementSetItems = await getSetPhrases(activeBoostSet.id);
      dispatch(
        updateBoosts({
          activeBoostSetShared: activeBoostSet.visibility !== PhraseVisibility.PERSONAL && activeBoostSet.isOwner,
          originalActiveBoostSetShared:
            activeBoostSet.visibility !== PhraseVisibility.PERSONAL && activeBoostSet.isOwner,
          activeBoostSetItems: replacementSetItems.data,
          originalActiveBoostSetItems: replacementSetItems.data,
        })
      );
    } catch (error) {
      changeToGroupsType();
    }
  };

  const handleActiveBoostSetTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeBoostSet) return;
    if (event.target.value.length === 255) {
      enqueueSnackbar('Maksimalna dolžina imena je 255 znakov.', { variant: 'error' });
      return;
    }
    dispatch(updateBoosts({ activeBoostSet: { ...activeBoostSet, name: event.target.value } }));
  };

  const toggleActiveBoostSetIsShared = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateBoosts({ activeBoostSetShared: event.target.checked }));
  };

  const handleNewBoostText = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateBoosts({ newBoostText: event.target.value }));
  };
  const handleNewBoostValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateBoosts({ newBoostValue: event.target.value }));
  };

  const addBoostToSet = () => {
    let alreadyExist = false;
    activeBoostSetItems.forEach((boost) => {
      if (boost.text === newBoostText) {
        alreadyExist = true;
      }
    });

    if (alreadyExist) {
      enqueueSnackbar(`Utež za "${newBoostText}" že obstaja`, { variant: 'error' });
      return;
    }

    const copy = cloneDeep(activeBoostSetItems);
    copy.unshift({
      id: -DateTime.now().toMillis(),
      text: newBoostText,
      boost: +newBoostValue,
    });
    dispatch(
      updateBoosts({
        activeBoostSetItems: copy,
        newBoostText: '',
        newBoostValue: '',
      })
    );
  };

  const updateSetItemText = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const copy = cloneDeep(activeBoostSetItems);
    copy[index].text = event.target.value;
    dispatch(updateBoosts({ activeBoostSetItems: copy }));
  };
  const updateSetItemValue = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const copy = cloneDeep(activeBoostSetItems);
    copy[index].boost = +event.target.value;
    dispatch(updateBoosts({ activeBoostSetItems: copy }));
  };

  const removeBoostItemFromSet = (index: number) => {
    const copy = cloneDeep(activeBoostSetItems);
    copy.splice(index, 1);
    dispatch(updateBoosts({ activeBoostSetItems: copy }));
  };

  const handleTsvUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const importBoostTsv = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.trim().split('\n');

      const importedBoosts: SetPhrases = [];

      if (lines.length < 2) {
        enqueueSnackbar('Datoteka ni v veljavnem formatu!', { variant: 'error' });
        return;
      }

      const line = lines[0];
      const splitter = /\t/;
      const [headerBoostText, headerBoostValue] = line.split(splitter, 2);
      if (headerBoostText !== 'phrase' && headerBoostValue !== 'boost') {
        enqueueSnackbar('Datoteka ni v veljavnem formatu!', { variant: 'error' });
        return;
      }
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const splitter = /\t/;
        const [rawText, rawBoost] = line.split(splitter, 2);

        const importedBoost: IBoostPhrase = {
          id: -(Date.now() + i),
          text: rawText,
          boost: +rawBoost,
        };

        importedBoosts.push(importedBoost);
      }

      console.log(importedBoosts);
      console.log(activeBoostSetItems);

      const newList: SetPhrases = [];
      importedBoosts.forEach((newBoost) => {
        const replacedIndex = activeBoostSetItems.findIndex((oldBoost) => {
          return newBoost.text === oldBoost.text;
        });
        if (replacedIndex < 0) {
          newList.push(newBoost);
        } else {
          newList[replacedIndex].boost = newBoost.boost;
        }
      });
      console.log(newList);

      dispatch(updateBoosts({ activeBoostSetItems: newList }));
      event.target.value = '';
    };

    reader.readAsText(file);
  };

  const changeToGroupsType = () => {
    dispatch(setBoostsModal({ type: BoostsModalType.sets }));
    dispatch(
      updateBoosts({
        activeBoostSet: undefined,
        originalActiveBoostSetItems: undefined,
        activeBoostSetItems: [],
        //origi: [],
        activeBoostSetShared: false,
        boostSets: undefined,
        newBoostText: '',
        newBoostValue: '',
      })
    );
  };

  const createNewSet = async () => {
    if (!activeBoostSet) return;

    try {
      const response = await postPhraseSet({
        isShared: activeBoostSetShared,
        name: activeBoostSet.name,
        type: PhraseType.BOOST,
      });
      const setId = response.data.id;
      await createBoostPhraseItems({ id: setId, items: activeBoostSetItems });
      //This means that replacements where opened from editor
      //in this case modal should be hidden after editor process current source as replacement and not here.
      //somehow existing function in editor should be triggered so that replacement is visible in existing editor and after processing aswell.

      /*if (replacementSourceText) {
        dispatch(updateReplacements({ triggerEditorReplacement: true }));
        return;
      }*/

      changeToGroupsType();
    } catch (error) {
      console.log(error);
    }
  };

  const updateExistingSet = async () => {
    if (!activeBoostSet) return;

    try {
      const setId = activeBoostSet.id;
      const originalItemIds = originalActiveBoostSetItems.map((item) => item.id);
      const currentItemIds = activeBoostSetItems.map((item) => item.id);

      const deleteItemIds = originalItemIds.filter((id) => !currentItemIds.includes(id));
      const addItemIds = currentItemIds.filter((id) => !originalItemIds.includes(id));
      const commonItemIds = originalItemIds.filter((id) => currentItemIds.includes(id));

      const elementsToAdd = activeBoostSetItems.filter((item) => addItemIds.includes(item.id));
      const changedItems = commonItemIds
        .map((id) => {
          const originalItem = originalActiveBoostSetItems.find((item) => item.id === id);
          const currentItem = activeBoostSetItems.find((item) => item.id === id);

          if (originalItem && currentItem) {
            const textChanged = originalItem.text !== currentItem.text;
            const boostChanged = originalItem.boost !== currentItem.boost;

            if (textChanged || boostChanged) {
              return currentItem;
            }
          }
          return null;
        })
        .filter((item) => item !== null);

      const requests = updateBoostSetItems({
        id: setId,
        toCreateItems: elementsToAdd,
        toUpdateItems: changedItems,
        toDeleteIndexes: deleteItemIds,
      });

      await patchPhraseSetById({
        id: setId,
        isShared: activeBoostSetShared,
        name: originalActiveBoostSet?.name === activeBoostSet.name ? undefined : activeBoostSet.name,
      });

      for (let requestIndex = 0; requestIndex < requests.length; requestIndex++) {
        const request = requests[requestIndex];
        await request();
      }

      changeToGroupsType();
    } catch (error) {
      console.log(error);
    }
  };

  const deleteSet = async () => {
    if (!activeBoostSet) return;
    try {
      await deletePhraseSet(activeBoostSet.id);
      changeToGroupsType();
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    //if (replacementSourceText) dispatch(updateReplacements({ newSourceText: replacementSourceText }));

    if (visible && type === BoostsModalType.sets) {
      setTimeout(() => fetchBoostSets(), 250);
    } else if (visible && type === BoostsModalType.edit_set) {
      setTimeout(() => fetchBoostSet(), 250);
    }
  }, [type, visible /*, replacementSourceText*/]);

  return (
    <Modal visible={visible} closeModal={hideBoostModal} title="Osebni slovar">
      <>
        {type === BoostsModalType.sets && (
          <div id="replacement_modal_content_container">
            <button onClick={changeToCreateSetType}>
              <div id="create_group_button_container">
                <Add sx={{ color: '#31a0ff' }} />
                <p>Ustvari skupino</p>
              </div>
            </button>

            <div id="replacement_filter_container">
              <Search />
              <input
                id="replacement_search_input"
                placeholder="Išči..."
                value={boostSetsFilter}
                onChange={updateBoostSetFilter}
              />
            </div>

            {(boostSets && (
              <div id="replacements_container">
                {boostSets
                  .filter((boostSet) => boostSet.set.name.includes(boostSetsFilter))
                  .filter((boostSet) => boostSet.set.visibility !== PhraseVisibility.GLOBAL)
                  .map((replacementSet, index) => {
                    const { checked, set } = replacementSet;
                    return (
                      <div className="replacement_wrapper" id={set.id.toString()}>
                        <div className="replacement_name_wrapper">
                          <input type="checkbox" checked={checked} onChange={() => toggleBoostSet(index)} />
                          <Tooltip
                            disableHoverListener={!set.isOwner}
                            disableFocusListener={!set.isOwner}
                            disableTouchListener={!set.isOwner}
                            title="Uredi skupino"
                            arrow
                            disableInteractive
                            placement="left"
                          >
                            <button className="replacement_name_wrapper" onClick={() => changeToEditSetType(set)}>
                              {(set.visibility === PhraseVisibility.PERSONAL && <PersonIcon />) ||
                                (set.visibility === PhraseVisibility.SHARED && <GroupsIcon />) ||
                                (set.visibility === PhraseVisibility.GLOBAL && <PublicIcon />)}
                              <p>{set.name}</p>
                            </button>
                          </Tooltip>
                        </div>
                        {set.isOwner && (
                          <Tooltip arrow disableInteractive placement="right" title="Izbriši skupino">
                            <button className="delete_button_container" onClick={() => changeToDeleteSetType(set)}>
                              <DeleteIcon />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
              </div>
            )) || (
              <div id="replacements_loader_container">
                <span id="replacements_loader" />
              </div>
            )}
          </div>
        )}

        {type === BoostsModalType.create_set && activeBoostSet && (
          <div id="replacement_modal_content_container">
            <div id="create_replacement_name_input_container">
              <input
                maxLength={255}
                id="create_replacement_name_input"
                placeholder="Vnesi ime skupine"
                value={activeBoostSet.name}
                onChange={handleActiveBoostSetTitle}
              />
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <p>Deli: </p>
                <input
                  type="checkbox"
                  checked={activeBoostSetShared}
                  id="create_replacement_is_shared_input"
                  onChange={toggleActiveBoostSetIsShared}
                />
              </div>
            </div>

            <div id="replacement_filter_container">
              <Search />
              <input
                id="replacement_search_input"
                placeholder="Išči..."
                value={boostActiveSetFilter}
                onChange={updateActiveBoostSetFilter}
              />
            </div>

            <div id="replacement_table_container">
              <table className="replacement_table">
                <thead className="replacement_table_head">
                  <tr className="replacement_table_head_tr">
                    <th className="replacemenet_table_head_th">Tekst</th>
                    <th className="replacemenet_table_head_th">Utež</th>
                    <th className="replacemenet_table_head_th">Akcije</th>
                  </tr>
                </thead>
                <tbody className="replacement_table_body">
                  <tr className="replacement_table_tr">
                    <td className="replacement_table_td">
                      <input placeholder="Vnesi besedilo" value={newBoostText} onChange={handleNewBoostText} />
                    </td>
                    <td className="replacement_table_td">
                      <input
                        type="number"
                        placeholder="Vnesi utež"
                        value={newBoostValue}
                        onChange={handleNewBoostValue}
                      />
                    </td>
                    <td className="replacement_table_td">
                      <Tooltip arrow disableInteractive placement="right" title="Dodaj utež">
                        <button
                          onClick={addBoostToSet}
                          style={{ width: '100%', height: '100%', placeContent: 'center' }}
                        >
                          <Add />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                  {activeBoostSetItems
                    .filter((item) => item.text.includes(boostActiveSetFilter))
                    .map((item, index) => {
                      return (
                        <tr key={index} className="replacement_table_tr">
                          <td className="replacement_table_td">
                            <input
                              placeholder="Vnesi besedilo"
                              value={item.text}
                              onChange={(event) => updateSetItemText(event, index)}
                            />
                          </td>
                          <td className="replacement_table_td">
                            <input
                              type="number"
                              placeholder="Vnesi besedilo"
                              value={item.boost}
                              onChange={(event) => updateSetItemValue(event, index)}
                            />
                          </td>
                          <td className="replacement_table_td">
                            <Tooltip arrow disableInteractive placement="right" title="Izbriši utež">
                              <button
                                onClick={() => removeBoostItemFromSet(index)}
                                style={{ width: '100%', height: '100%', placeContent: 'center' }}
                              >
                                <Delete className="delete_button_entry" />
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <Tooltip
              title={
                <>
                  <Typography align="left">Pričakovani format .tsv datoteke</Typography>
                  <Typography align="left">phrase,boost</Typography>
                  <Typography align="left">"Danes","0.2"</Typography>
                  <Typography align="left">"Jutri","-0.4"</Typography>
                </>
              }
            >
              <button style={{ marginTop: 24 }} onClick={handleTsvUploadClick}>
                <div id="create_group_button_container">
                  <UploadFile sx={{ color: '#31a0ff' }} />
                  <p>NALOŽI</p>
                </div>
              </button>
            </Tooltip>

            <input ref={fileInputRef} type="file" accept=".tsv" onChange={importBoostTsv} style={{ display: 'none' }} />

            <div id="replacements_modal_footer">
              <button id="replacements_modal_close_button" onClick={changeToGroupsType}>
                NAZAJ
              </button>
              <button
                disabled={activeBoostSetItems.length < 1 || activeBoostSet.name.length === 0}
                id="replacements_modal_save_button"
                onClick={createNewSet}
              >
                USTVARI
              </button>
            </div>
          </div>
        )}

        {type === BoostsModalType.edit_set && activeBoostSet && (
          <div id="replacement_modal_content_container">
            <div id="create_replacement_name_input_container">
              <input
                maxLength={255}
                id="create_replacement_name_input"
                placeholder="Vnesi ime skupine"
                value={activeBoostSet.name}
                onChange={handleActiveBoostSetTitle}
              />
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <p>Deli: </p>
                <input
                  type="checkbox"
                  checked={activeBoostSetShared}
                  id="create_replacement_is_shared_input"
                  onChange={toggleActiveBoostSetIsShared}
                />
              </div>
            </div>

            <div id="replacement_filter_container">
              <Search />
              <input
                id="replacement_search_input"
                placeholder="Išči..."
                value={boostActiveSetFilter}
                onChange={updateActiveBoostSetFilter}
              />
            </div>

            <div id="replacement_table_container">
              <table className="replacement_table">
                <thead className="replacement_table_head">
                  <tr className="replacement_table_head_tr">
                    <th className="replacemenet_table_head_th">Tekst</th>
                    <th className="replacemenet_table_head_th">Utež</th>
                    <th className="replacemenet_table_head_th">Akcije</th>
                  </tr>
                </thead>
                <tbody className="replacement_table_body">
                  <tr className="replacement_table_tr">
                    <td className="replacement_table_td">
                      <input placeholder="Vnesi besedilo" value={newBoostText} onChange={handleNewBoostText} />
                    </td>
                    <td className="replacement_table_td">
                      <input
                        type="number"
                        placeholder="Vnesi utež"
                        value={newBoostValue}
                        onChange={handleNewBoostValue}
                      />
                    </td>
                    <td className="replacement_table_td">
                      <Tooltip arrow disableInteractive placement="right" title="Dodaj zamenjavo">
                        <button
                          onClick={addBoostToSet}
                          style={{ width: '100%', height: '100%', placeContent: 'center' }}
                        >
                          <Add />
                        </button>
                      </Tooltip>
                    </td>
                  </tr>
                  {activeBoostSetItems
                    .filter((item) => item.text.includes(boostActiveSetFilter))
                    .map((item, index) => {
                      return (
                        <tr key={index} className="replacement_table_tr">
                          <td className="replacement_table_td">
                            <input
                              placeholder="Vnesi besedilo"
                              value={item.text}
                              onChange={(event) => updateSetItemText(event, index)}
                            />
                          </td>
                          <td className="replacement_table_td">
                            <input
                              type="number"
                              placeholder="Vnesi utež"
                              value={item.boost}
                              onChange={(event) => updateSetItemValue(event, index)}
                            />
                          </td>
                          <td className="replacement_table_td">
                            <Tooltip arrow disableInteractive placement="right" title="Izbriši zamenjavo">
                              <button
                                style={{ width: '100%', height: '100%', placeContent: 'center' }}
                                onClick={() => removeBoostItemFromSet(index)}
                              >
                                <Delete className="delete_button_entry" />
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <Tooltip
              title={
                <>
                  <Typography align="left">Pričakovani format .tsv datoteke</Typography>
                  <Typography align="left">phrase,boost</Typography>
                  <Typography align="left">"Danes","0.2"</Typography>
                  <Typography align="left">"Jutri","-0.4"</Typography>
                </>
              }
            >
              <button style={{ marginTop: 24 }} onClick={handleTsvUploadClick}>
                <div id="create_group_button_container">
                  <UploadFile sx={{ color: '#31a0ff' }} />
                  <p>NALOŽI</p>
                </div>
              </button>
            </Tooltip>

            <input ref={fileInputRef} type="file" accept=".csv" onChange={importBoostTsv} style={{ display: 'none' }} />

            <div id="replacements_modal_footer">
              <button id="replacements_modal_close_button" onClick={changeToGroupsType}>
                NAZAJ
              </button>
              <button
                disabled={activeBoostSetItems.length < 1 || activeBoostSet.name.length === 0}
                id="replacements_modal_save_button"
                onClick={updateExistingSet}
              >
                SHRANI
              </button>
            </div>
          </div>
        )}

        {type === BoostsModalType.delete_set && activeBoostSet && (
          <div id="replacement_modal_content_container">
            <p id="replacement_delete_text">
              Ali ste prepričani, da želite izbrisati skupino <strong>{activeBoostSet.name}</strong>?
            </p>
            <div id="replacements_modal_footer">
              <button id="replacements_modal_close_button" onClick={changeToGroupsType}>
                NAZAJ
              </button>
              <button id="replacements_modal_delete_button" onClick={deleteSet}>
                IZBRIŠI
              </button>
            </div>
          </div>
        )}
      </>
    </Modal>
  );
};

export default BoostModal;
